
import { useEffect, useRef } from 'react';
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Testimonials from "@/components/Testimonials";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';

const Home = () => {
  const contactRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Initialize scroll animation observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));

    // Check if URL has #contact hash
    if (window.location.hash === '#contact' && contactRef.current) {
      setTimeout(() => {
        contactRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  // Nouvelle introduction avec les deux publics
  const NewIntroduction = () => (
    <section className="section-padding bg-background">
      <div className="max-w-4xl mx-auto text-center reveal-on-scroll">
        <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">✨ Bienvenue chez Meneora</h2>
        <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">Coaching, Hypnose & Soins Énergétiques</h3>
        <p className="text-xl mb-6">Un espace pour retrouver ta lumière.<br />Un lieu d'alignement, de réparation, de vérité.</p>
        
        <div className="mb-8">
          <p className="text-lg mb-4">🌿 Accompagnement vibratoire pour celles et ceux qui portent beaucoup</p>
          <p className="text-lg mb-4">Hypnose intuitive, soins énergétiques, cercles et nettoyage vibratoire des lieux.</p>
          <p className="text-lg mb-6">À Étampes (91) ou à distance, en visio ou soin vibratoire.</p>
        </div>

        <h3 className="text-2xl font-semibold text-primary mb-8">🔍 Qui es-tu ? Vers où souhaites-tu aller ?<br />Choisis le chemin qui te correspond :</h3>
        
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="bg-accent/10 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-primary mb-4">💫 Je suis un particulier</h4>
            <p className="text-base mb-6">Tu traverses une fatigue, une confusion, une perte d'élan ?<br />Tu sens qu'il est temps d'y voir clair, de revenir à toi, ou de te libérer d'un blocage ?<br />Hypnose, soins énergétiques, libérations transgénérationnelles, accompagnement doux et profond.</p>
            <Link to="/particulier">
              <Button size="lg" className="w-full">
                🕊️ Découvrir l'espace particulier
              </Button>
            </Link>
          </div>
          
          <div className="bg-primary/10 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-primary mb-4">🌟 Je suis un professionnel de l'accompagnement</h4>
            <p className="text-base mb-6">Tu es thérapeute, soignant, enseignant, coach ?<br />Tu donnes beaucoup, mais tu sens un besoin urgent de te réaligner, te nettoyer, ou simplement recevoir ?<br />Je propose des soins pour les pros, des bilans vibratoires, du soutien énergétique, et des cercles entre praticiens.</p>
            <Link to="/professionnel">
              <Button size="lg" className="w-full">
                🔥 Découvrir l'espace professionnel
              </Button>
            </Link>
          </div>
        </div>

        <p className="text-sm text-foreground/70">
          <em>Séances en présentiel à Étampes (91) ou à distance, en visio ou soin vibratoire.</em>
        </p>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <Hero />
      <NewIntroduction />
      
      {/* CancellationWarning */}
      <div className="py-6 bg-background">
        <p className="text-center text-foreground/70 text-base font-medium max-w-4xl mx-auto reveal-on-scroll">
          <strong>⚠️ Toute séance annulée moins de 48h à l'avance est considérée comme due et non remboursable. Merci de réserver en conscience 🌿</strong>
        </p>
      </div>
      
      {/* AboutBrief */}
      <section id="je-suis-meredith-meneora" className="section-padding bg-accent/10">
        <div className="max-w-7xl mx-auto reveal-on-scroll">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div className="text-center md:text-left">
              <h2 className="section-title mb-6">Je suis Meredith Meneora.</h2>
              <p className="text-xl mb-6">
                J'accompagne celles et ceux qui portent beaucoup…<br />
                …et qui, parfois, ont besoin de déposer.
              </p>
              <p className="mb-6">
                Que tu sois thérapeute, soignant, parent, hypersensible ou en quête de sens,<br />
                tu es peut-être celui ou celle qui tient bon. Qui soutient, écoute, agit…<br />
                Mais qui, en silence, s'oublie.
              </p>
              <p className="mb-6">
                Je t'ouvre un espace pour revenir à toi.<br />
                Un lieu d'alignement, de réparation, de vérité.
              </p>
              <p className="mb-6">
                Mon approche est intuitive, vibratoire, profondément incarnée.<br />
                Ici, pas de méthode toute faite.<br />
                Juste une présence. Une écoute. Une guidance ancrée dans le subtil et le vrai.
              </p>
              <p className="mb-6">
                Tu peux venir tel que tu es.<br />
                Fatigué, flou, en pleine mutation ou prêt à renaître.<br />
                Tu n'as rien à prouver.<br />
                Tu peux respirer. Sentir. Revenir à ton axe.
              </p>
              <p className="text-lg">
                ✨ Bienvenue dans cet espace de reconnexion.<br />
                Il existe un lieu pour toi aussi. Tu n'es pas seul.
              </p>
            </div>
            <div className="flex justify-center md:justify-end">
              <img 
                src="/lovable-uploads/72665b79-799a-4fe6-a0d6-6e41baf738ec.png" 
                alt="Portrait de Meredith Meneora, accompagnante en hypnose et soins énergétiques"
                className="w-64 h-auto object-cover rounded-lg shadow-md"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* ContactSection */}
      <section id="contact" ref={contactRef} className="section-padding bg-background">
        <div className="max-w-4xl mx-auto text-center reveal-on-scroll">
          <h2 className="section-title mb-6">📞 Contact</h2>
          <p className="text-xl mb-4">📱 06 07 50 74 12 Uniquement joignable via WhatsApp</p>
          <p className="text-lg mb-6">
            N'hésite pas à m'écrire.<br />
            Je te répondrai dès que possible.
          </p>
          <a href="https://wa.me/33607507412" target="_blank" rel="noopener noreferrer">
            <Button variant="default">
              Écrire sur WhatsApp
            </Button>
          </a>
        </div>
      </section>
      
      <Testimonials />
      
      {/* DownloadableResource */}
      <section className="section-padding bg-accent/10">
        <div className="max-w-7xl mx-auto reveal-on-scroll">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div className="flex justify-center md:justify-start">
              <img 
                src="/lovable-uploads/3b708940-e395-4602-bf56-69aa2a5f74fe.png" 
                alt="Couverture du livret de rituels et décrets par Meredith Meneora"
                className="w-80 h-auto object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="section-title mb-6">Livret de rituels & décrets d'alignement</h2>
              <p className="text-lg mb-4">
                Un petit livret vibratoire, à télécharger, pour t'accompagner dans tes rituels, tes soins, ou tes instants à toi.
              </p>
              <p className="text-lg mb-6">
                ✨ Des mots alignés à ton axe, pour ouvrir l'espace, poser l'intention, vibrer ton chemin.
              </p>
              <div className="mb-6">
                <p className="text-lg mb-2">💰 Tarif : 15 €</p>
                <p className="text-lg">📎 Format : PDF téléchargeable</p>
              </div>
              <a href="https://payhip.com/b/n4mBO" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="w-full md:w-auto">
                  Télécharger
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* CallToAction */}
      <section id="appointment" className="section-padding bg-primary/5">
        <div className="max-w-3xl mx-auto text-center reveal-on-scroll">
          <h2 className="section-title mb-4">Prêt à te recentrer et rayonner ?</h2>
          <p className="text-xl mb-8">
            Réserve ton soin, ton hypnose ou rejoins un cercle.<br />
            En visio ou à Étampes.
          </p>
          <a href="https://calendly.com/meneora" target="_blank" rel="noopener noreferrer">
            <Button size="lg">
              Je prends rendez-vous
            </Button>
          </a>
        </div>
      </section>
      
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Home;
